import React, { useState } from "react";
import { TrainingLog, ActivityLog } from "../../../types/post";
import Sparkline from "./Sparkline";
import {
  useLogCardActions,
  LogCardActionsProps,
} from "../../../hooks/useLogCardActions";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";

interface TrainingSessionCardProps extends LogCardActionsProps<
  TrainingLog & { id: string }
> {
  trainingLog: TrainingLog & { id: string };
  linkedActivity?: (ActivityLog & { id: string }) | null;
  onClick?: (trainingLogId: string) => void;
  onDelete?: (logId: string) => void;
  onEdit?: (logId: string, log: TrainingLog & { id: string }) => void;
  className?: string;
}

const formatMinutes = (mins?: number) => {
  if (!mins || mins <= 0) return "";
  return `${Math.round(mins)} minutes`;
};

const TrainingSessionCard: React.FC<TrainingSessionCardProps> = ({
  trainingLog,
  linkedActivity,
  onClick,
  onDelete,
  onEdit,
  className = "",
}) => {
  const { userData } = useAuth();
  const [showActions, setShowActions] = useState(false);
  const { handleDelete, handleEdit } = useLogCardActions(trainingLog, {
    onDelete,
    onEdit,
  });

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking on actions menu
    if ((e.target as HTMLElement).closest(".actions-menu")) {
      return;
    }
    if (onClick) onClick(trainingLog.id);
  };

  const handleActionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowActions(!showActions);
  };

  const isClickable = !!onClick;
  const hasActions = !!(onDelete || onEdit);
  const Tag: any = isClickable ? "button" : "div";

  // Minimal HR graph: prefer detailed samples; fallback to summary if available
  const hrSamples = linkedActivity?.heartRateData?.detailed?.hr_samples || [];
  const summaryAvg = linkedActivity?.heartRateData?.summary?.avg_hr_bpm;
  const miniSeries =
    hrSamples.length > 0
      ? hrSamples
          .map((s) => (typeof s.bpm === "number" ? s.bpm : NaN))
          .filter((v) => Number.isFinite(v))
      : typeof summaryAvg === "number"
        ? [summaryAvg, summaryAvg]
        : [];

  return (
    <div className="relative">
      <Tag
        onClick={handleClick}
        className={`w-full text-left bg-base-200 p-2 px-4 rounded-md ${
          isClickable
            ? "hover:border hover:border-1 hover:border-base-300 transition-colors cursor-pointer"
            : ""
        } ${className}`}
      >
        {/* Header with actions */}
        <div className="flex items-start justify-between mb-2 pt-2">
          <div className="flex-1">
            {/* Training Tags */}
            {trainingLog.tag && trainingLog.tag.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {trainingLog.tag.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold bg-base-100 border-base-300 border border-1 text-base-content font-geist"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions Menu */}
          {hasActions && (
            <div className="actions-menu relative">
              <button
                onClick={handleActionsClick}
                className="p-1 hover:bg-base-200 rounded-full transition-colors"
              >
                <EllipsisVerticalIcon className="h-4 w-4 text-base-content/60" />
              </button>

              {showActions && (
                <div className="absolute right-0 top-8 bg-base-100 border border-base-200 rounded-md shadow-lg z-10 min-w-[120px]">
                  {onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit();
                        setShowActions(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-base-100 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                        setShowActions(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-500/10 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-sm text-base-content/80">{trainingLog.notes}</p>
        <div className="flex items-center justify-between gap-4">
          {/* Hide load value for casual users */}
          {!userData?.casualMode && (
            <div className="flex flex-col min-w-0 gap-0">
              <div className=" font-bold text-base-content font-heading mt-0.5">
                {Math.round(trainingLog.load || 0)}
                <span className="font-light font-grotesk ml-1">load</span>
              </div>
            </div>
          )}

          {miniSeries.length > 0 && (
            <Sparkline
              values={miniSeries}
              width={120}
              height={36}
              strokeClassName="stroke-gray-300"
            />
          )}
        </div>
      </Tag>

      {/* Click outside to close actions menu */}
      {showActions && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActions(false)}
        />
      )}
    </div>
  );
};

export default TrainingSessionCard;
